import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, searchOpen]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
      isActive
        ? "text-accent-400"
        : "text-background-100/85 hover:text-background-50"
    }`;

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? "bg-primary-950 shadow-[0_1px_0_0_rgba(255,255,255,0.06)]"
            : "bg-primary-900"
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <div className="h-16 md:h-[68px] flex items-center justify-between gap-4">
            <Link
              to="/"
              className="flex items-center gap-2.5 shrink-0 cursor-pointer"
            >
              <span className="w-9 h-9 rounded-lg bg-accent-500 text-accent-950 flex items-center justify-center text-xl">
                <i className="ri-play-circle-fill"></i>
              </span>
              <span className="flex flex-col leading-none">
                <span className="text-lg font-extrabold text-background-50 tracking-tight">
                  99视频
                </span>
                <span className="text-[10px] text-background-200/70 tracking-[0.18em] mt-0.5">
                  HD VIDEO
                </span>
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 h-9 pl-3 pr-3 md:pr-4 rounded-full bg-background-50/10 text-background-100/80 hover:bg-background-50/20 transition-colors cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-base"></i>
                <span className="hidden md:inline text-sm">搜索影视</span>
              </button>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `hidden md:inline-flex items-center h-9 px-4 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-accent-500 text-accent-950"
                      : "text-background-100/85 hover:text-background-50"
                  }`
                }
              >
                关于我们
              </NavLink>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-background-50 text-xl cursor-pointer"
                aria-label="菜单"
              >
                <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
              </button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-primary-950 border-t border-background-50/10 animate-fade-in">
            <nav className="px-4 py-3 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-2 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      isActive
                        ? "text-accent-400 bg-background-50/5"
                        : "text-background-100/85"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="px-2 py-3 rounded-md text-sm font-medium text-background-100/85 cursor-pointer"
              >
                关于我们
              </NavLink>
            </nav>
          </div>
        )}
      </header>

      {searchOpen && (
        <div className="fixed inset-0 z-[60] animate-fade-in">
          <div
            className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
            onClick={() => setSearchOpen(false)}
          ></div>
          <div className="relative w-full max-w-2xl mx-auto mt-24 md:mt-32 px-4">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 bg-background-50 rounded-lg p-2 pl-4 shadow-xl"
            >
              <i className="ri-search-line text-xl text-foreground-500"></i>
              <input
                autoFocus
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、电视剧、综艺…"
                className="flex-1 min-w-0 h-11 text-base text-foreground-900 bg-transparent outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="h-11 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
            <div className="mt-4 flex flex-wrap items-center gap-2 px-1">
              <span className="text-sm text-background-200">热门搜索：</span>
              {["星际远征", "长安迷雾", "星光舞台", "悬疑", "科幻"].map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => setKeyword(kw)}
                  className="px-3 py-1 rounded-full text-sm bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}